import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert
} from '@mui/material';
import API_CONFIG from '../../config/api';
import { TEST_CASE_GENERATION_CONFIG } from '../../config/testCaseGenerationConfig';

/**
 * Normalize bulk-save payloads across new (status/counts) and older (success-only) shapes.
 */
export function interpretBulkSaveOutcome(saveData) {
  const data = saveData && typeof saveData === 'object' ? saveData : {};
  const errors = Array.isArray(data.errors) ? data.errors : [];
  const requestedCount =
    typeof data.requested_count === 'number'
      ? data.requested_count
      : (typeof data.total_requested === 'number' ? data.total_requested : 0);
  const savedCount = typeof data.saved_count === 'number' ? data.saved_count : 0;
  const errorCount =
    typeof data.error_count === 'number' ? data.error_count : errors.length;

  let status = data.status;
  if (status !== 'complete' && status !== 'partial' && status !== 'failed') {
    if (savedCount === requestedCount && errorCount === 0) {
      status = 'complete';
    } else if (savedCount > 0 && (savedCount < requestedCount || errorCount > 0)) {
      status = 'partial';
    } else if (savedCount === 0 && requestedCount > 0) {
      status = 'failed';
    } else if (data.success === true && savedCount > 0 && errorCount === 0) {
      // Older clients claimed success even with errors; prefer counts when present.
      status = 'complete';
    } else if (savedCount > 0) {
      status = 'partial';
    } else {
      status = 'failed';
    }
  }

  return {
    status,
    requestedCount,
    savedCount,
    errorCount,
    errors,
    generationRunId: data.generation_run_id || null,
  };
}

/**
 * Modal for generating and saving test cases with progress indication.
 * All generated test cases are auto-saved; no per-row selection.
 */
const GenerateTestCasesModal = ({
  open,
  onClose,
  projectId,
  jiraKey,
  storyTitle,
  count = TEST_CASE_GENERATION_CONFIG.DEFAULT_COUNT,
  includeNegative = TEST_CASE_GENERATION_CONFIG.DEFAULT_INCLUDE_NEGATIVE,
  includeIntegration = TEST_CASE_GENERATION_CONFIG.DEFAULT_INCLUDE_INTEGRATION,
  onTestCasesSaved
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [partial, setPartial] = useState(null);
  const [savedCount, setSavedCount] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const messageIntervalRef = useRef(null);

  // Rotate through witty messages while loading
  useEffect(() => {
    if (loading) {
      // Start with first message
      setCurrentMessageIndex(0);
      
      // Rotate messages at configured interval
      messageIntervalRef.current = setInterval(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % TEST_CASE_GENERATION_CONFIG.PROGRESS_MESSAGES.length);
      }, TEST_CASE_GENERATION_CONFIG.MESSAGE_ROTATION_INTERVAL_MS);
    } else {
      // Clear interval when not loading
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
    }

    return () => {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
      }
    };
  }, [loading]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setError(null);
      setSuccess(false);
      setPartial(null);
      setSavedCount(0);
      setCurrentMessageIndex(0);
    }
  }, [open]);

  const handleGenerateAndSave = async () => {
    console.log('handleGenerateAndSave called:', { projectId, jiraKey, open });
    if (!projectId || !jiraKey) {
      console.error('Missing required params:', { projectId, jiraKey });
      setError('Project ID and JIRA key are required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setPartial(null);
    setSavedCount(0);

    try {
      const token = localStorage.getItem('stratovai_access_token');
      
      console.log('Generating test cases...');
      const generateRes = await fetch(
        API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.GENERATE_TEST_CASES(projectId, jiraKey)),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            count,
            include_negative: includeNegative,
            include_integration: includeIntegration,
            focus_impacted_stories: true
          })
        }
      );

      if (!generateRes.ok) {
        const errorData = await generateRes.json().catch(() => ({ detail: 'Failed to generate test cases' }));
        throw new Error(errorData.detail || 'Failed to generate test cases');
      }

      const generateData = await generateRes.json();
      if (!generateData.success || !generateData.suggestions || generateData.suggestions.length === 0) {
        const errorMsg = generateData.detail || `No test cases generated. ${generateData.count || 0} test cases returned.`;
        throw new Error(errorMsg);
      }

      console.log(`Generated ${generateData.suggestions.length} test cases, now saving...`);

      // Save all generated test cases
      const saveRes = await fetch(
        API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.BULK_SAVE_TEST_CASES(projectId)),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            test_cases: generateData.suggestions
          })
        }
      );

      if (!saveRes.ok) {
        const errorData = await saveRes.json().catch(() => ({ detail: 'Failed to save test cases' }));
        throw new Error(errorData.detail || 'Failed to save test cases');
      }

      const saveData = await saveRes.json();
      const outcome = interpretBulkSaveOutcome(saveData);

      if (outcome.status === 'complete') {
        console.log(`Successfully saved ${outcome.savedCount} test cases`);
        setSuccess(true);
        setSavedCount(outcome.savedCount);

        if (onTestCasesSaved) {
          onTestCasesSaved({
            savedCount: outcome.savedCount,
            generationRunId: outcome.generationRunId,
          });
        }

        // Close after short delay so the user can read the success message
        setTimeout(() => {
          onClose();
        }, TEST_CASE_GENERATION_CONFIG.MODAL_CLOSE_DELAY_MS);
        return;
      }

      if (outcome.status === 'partial') {
        setPartial({
          requestedCount: outcome.requestedCount,
          savedCount: outcome.savedCount,
          errorCount: outcome.errorCount,
        });
        setSavedCount(outcome.savedCount);
        return;
      }

      // failed (including older payloads that claimed success with zero saves)
      const errorMsg =
        outcome.errors.length > 0
          ? outcome.errors.join(', ')
          : (saveData.detail || 'Failed to save test cases');
      throw new Error(errorMsg);

    } catch (e) {
      console.error('Test case generation/save error:', e);
      setError(e.message || 'Failed to generate and save test cases');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    setSuccess(false);
    setPartial(null);
    handleGenerateAndSave();
  };

  // Generate and save on mount when modal opens
  useEffect(() => {
    console.log('Modal useEffect:', { open, projectId, jiraKey, error, success, partial });
    if (open && projectId && jiraKey && !error && !success && !partial) {
      setLoading(true);
      const timer = setTimeout(() => {
        handleGenerateAndSave();
      }, 100);
      return () => clearTimeout(timer);
    } else if (open && (!projectId || !jiraKey)) {
      console.warn('Modal opened but missing required params:', { projectId, jiraKey });
      setError('Project ID and JIRA key are required');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId, jiraKey]);

  const outcomeSettled = success || Boolean(partial) || Boolean(error);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: TEST_CASE_GENERATION_CONFIG.MODAL_MIN_HEIGHT }
      }}
    >
      <DialogTitle>
        Generate Test Cases for {jiraKey}
        {storyTitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {storyTitle}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ py: 4, px: 2 }}>
            <LinearProgress sx={{ mb: 3 }} />
            <Typography
              variant="body1"
              align="center"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
                minHeight: '24px',
                fontSize: '1rem',
                fontWeight: 400
              }}
            >
              {TEST_CASE_GENERATION_CONFIG.PROGRESS_MESSAGES[currentMessageIndex] || TEST_CASE_GENERATION_CONFIG.PROGRESS_MESSAGES[0]}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {partial && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Partially saved test cases: {partial.savedCount} of {partial.requestedCount} saved
            ({partial.errorCount} failed). Some generated cases were not persisted.
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Successfully generated and saved {savedCount} test case{savedCount !== 1 ? 's' : ''}.
          </Alert>
        )}

        {!loading && !outcomeSettled && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No test cases generated. Please try again.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {success || partial ? 'Close' : 'Cancel'}
        </Button>
        {error && (
          <Button variant="contained" onClick={handleRetry}>
            Retry
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateTestCasesModal;
